<template>
  <div class="space-y-4">
    <div>
      <label class="block text-sm font-medium mb-2">
        {{ t('leadSourceIcons.selectLabel') }}
      </label>
      <div class="icon-grid-container">
        <div class="grid grid-cols-8 gap-2">
          <button
            v-for="icon in leadSourceIcons"
            :key="icon.value"
            @click="selectIcon(icon.value)"
            type="button"
            class="w-12 h-12 rounded-lg border-2 transition-all flex items-center justify-center hover:scale-105"
            :class="selectedIcon === icon.value 
              ? 'border-blue-500 bg-blue-500 bg-opacity-20 shadow-lg' 
              : 'border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50'"
            :disabled="disabled"
            :title="icon.label"
          >
            <LeadSourceIcons :name="icon.component" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import LeadSourceIcons from './icons/LeadSourceIcons.vue'

const { t } = useI18n()

const props = defineProps({
  modelValue: {
    type: String,
    default: 'website'
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const selectedIcon = ref(props.modelValue)

// Ícones SVG específicos para origens de lead - Lista otimizada
const leadSourceIconDefs = [
  // Redes Sociais Principais
  { 
    value: 'facebook',
    component: 'FacebookIcon'
  },
  { 
    value: 'instagram',
    component: 'InstagramIcon'
  },
  { 
    value: 'whatsapp',
    component: 'WhatsAppIcon'
  },
  { 
    value: 'linkedin',
    component: 'LinkedInIcon'
  },
  { 
    value: 'youtube',
    component: 'YouTubeIcon'
  },
  { 
    value: 'tiktok',
    component: 'TikTokIcon'
  },
  { 
    value: 'twitter',
    component: 'TwitterIcon'
  },
  { 
    value: 'telegram',
    component: 'TelegramIcon'
  },
  
  // Motores de Busca
  { 
    value: 'google',
    component: 'GoogleIcon'
  },
  { 
    value: 'bing',
    component: 'BingIcon'
  },
  
  // Websites e Internet
  { 
    value: 'website',
    component: 'WebsiteIcon'
  },
  { 
    value: 'internet',
    component: 'InternetIcon'
  },
  { 
    value: 'blog',
    component: 'BlogIcon'
  },
  { 
    value: 'landing-page',
    component: 'LandingPageIcon'
  },
  
  // E-commerce
  { 
    value: 'ecommerce',
    component: 'EcommerceIcon'
  },
  { 
    value: 'amazon',
    component: 'AmazonIcon'
  },
  { 
    value: 'mercadolivre',
    component: 'MercadoLivreIcon'
  },
  { 
    value: 'shopee',
    component: 'ShopeeIcon'
  },
  
  // Email e Marketing
  { 
    value: 'email',
    component: 'EmailIcon'
  },
  { 
    value: 'email-marketing',
    component: 'EmailMarketingIcon'
  },
  { 
    value: 'newsletter',
    component: 'NewsletterIcon'
  },
  { 
    value: 'webinar',
    component: 'WebinarIcon'
  },
  
  // Publicidade
  { 
    value: 'google-ads',
    component: 'GoogleAdsIcon'
  },
  { 
    value: 'facebook-ads',
    component: 'FacebookAdsIcon'
  },
  { 
    value: 'instagram-ads',
    component: 'InstagramAdsIcon'
  },
  { 
    value: 'affiliate',
    component: 'AffiliateIcon'
  },
  
  // Eventos
  { 
    value: 'event',
    component: 'EventIcon'
  },
  { 
    value: 'conference',
    component: 'ConferenceIcon'
  },
  { 
    value: 'workshop',
    component: 'WorkshopIcon'
  },
  { 
    value: 'networking',
    component: 'NetworkingIcon'
  },
  
  // Indicações
  { 
    value: 'referral',
    component: 'ReferralIcon'
  },
  { 
    value: 'word-of-mouth',
    component: 'WordOfMouthIcon'
  },
  { 
    value: 'partner',
    component: 'PartnerIcon'
  },
  
  // Canais Tradicionais
  { 
    value: 'phone',
    component: 'PhoneIcon'
  },
  { 
    value: 'street',
    component: 'StreetIcon'
  },
  { 
    value: 'walk-in',
    component: 'WalkInIcon'
  },
  { 
    value: 'store-visit',
    component: 'StoreVisitIcon'
  },
  { 
    value: 'office-visit',
    component: 'OfficeVisitIcon'
  },
  { 
    value: 'mail',
    component: 'MailIcon'
  },
  { 
    value: 'radio',
    component: 'RadioIcon'
  },
  { 
    value: 'tv',
    component: 'TvIcon'
  },
  { 
    value: 'newspaper',
    component: 'NewspaperIcon'
  },
  { 
    value: 'magazine',
    component: 'MagazineIcon'
  },
  { 
    value: 'billboard',
    component: 'BillboardIcon'
  },
  
  // Outros Digitais
  { 
    value: 'app-store',
    component: 'AppStoreIcon'
  },
  { 
    value: 'play-store',
    component: 'PlayStoreIcon'
  },
  { 
    value: 'qr-code',
    component: 'QrCodeIcon'
  },
  { 
    value: 'chatbot',
    component: 'ChatbotIcon'
  },
  { 
    value: 'live-chat',
    component: 'LiveChatIcon'
  },
  
  // Setores Específicos
  { 
    value: 'real-estate',
    component: 'RealEstateIcon'
  },
  { 
    value: 'automotive',
    component: 'AutomotiveIcon'
  },
  { 
    value: 'healthcare',
    component: 'HealthcareIcon'
  },
  { 
    value: 'education',
    component: 'EducationIcon'
  },
  { 
    value: 'finance',
    component: 'FinanceIcon'
  },
  { 
    value: 'insurance',
    component: 'InsuranceIcon'
  },
  { 
    value: 'legal',
    component: 'LegalIcon'
  },
  { 
    value: 'consulting',
    component: 'ConsultingIcon'
  },
  { 
    value: 'technology',
    component: 'TechnologyIcon'
  },
  { 
    value: 'retail',
    component: 'RetailIcon'
  },
  
  // Outros
  { 
    value: 'other',
    component: 'OtherIcon'
  },
  { 
    value: 'unknown',
    component: 'UnknownIcon'
  }
]

const leadSourceIcons = computed(() =>
  leadSourceIconDefs.map((icon) => ({
    ...icon,
    label: t(`leadSourceIcons.${icon.value}`, icon.value)
  }))
)

const selectIcon = (iconValue) => {
  if (props.disabled) return
  
  selectedIcon.value = iconValue
  emit('update:modelValue', iconValue)
}

// Sincronizar com prop externa
watch(() => props.modelValue, (newValue) => {
  selectedIcon.value = newValue
}, { immediate: true })
</script>

<style scoped>
/* Container com scroll para os ícones */
.icon-grid-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px;
  background-color: #f9fafb;
}

.icon-grid-container::-webkit-scrollbar {
  width: 6px;
}

.icon-grid-container::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.icon-grid-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.icon-grid-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Estilos para tema escuro */
.tema_escura .icon-grid-container {
  background-color: #1e2125;
  border-color: #36393f;
}

.tema_escura .icon-grid-container::-webkit-scrollbar-track {
  background: #2a2d31;
}

.tema_escura .icon-grid-container::-webkit-scrollbar-thumb {
  background: #4f5660;
}

.tema_escura .icon-grid-container::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Cores adaptáveis para os botões de ícones */
.tema_clara button {
  background: white;
  border-color: #d1d5db; /* gray-300 */
}

.tema_clara button:hover {
  background: #f9fafb; /* gray-50 */
  border-color: #9ca3af; /* gray-400 */
}

.tema_escura button {
  background: #374151; /* gray-700 */
  border-color: #4b5563; /* gray-600 */
}

.tema_escura button:hover {
  background: #4b5563; /* gray-600 */
  border-color: #6b7280; /* gray-500 */
}

/* Cores dos ícones dentro dos botões */
.tema_clara button svg {
  color: #1f2937; /* gray-800 - preto no tema claro */
}

.tema_escura button svg {
  color: #f9fafb; /* gray-50 - branco no tema escuro */
}
</style>
